// Admin-Panel › Logs: letzte Zeilen aus dem Ringpuffer der API, Level-Filter, Aktualisieren.
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { adminApi } from '../api';
import { Badge, Button, Empty, Field, Loading } from '../ui';
import { AdminError, AdminSection } from './parts';
import { ADMIN_LOGS_KEY } from './realtime';

const LEVELS = [
  { value: '', label: 'Alle' },
  { value: 'error', label: 'Fehler' },
  { value: 'warn', label: 'Warnungen' },
  { value: 'log', label: 'Info' },
  { value: 'debug', label: 'Debug' },
  { value: 'verbose', label: 'Ausführlich' },
] as const;
const LINE_OPTIONS = [100, 200, 500, 1000] as const;

function levelTone(level: string): 'neutral' | 'accent' | 'warn' | 'danger' {
  const l = level.toLowerCase();
  if (l === 'error' || l === 'fatal') return 'danger';
  if (l === 'warn' || l === 'warning') return 'warn';
  if (l === 'log' || l === 'info') return 'accent';
  return 'neutral';
}

export function LogsPage() {
  const [level, setLevel] = useState('');
  const [lines, setLines] = useState<number>(200);
  const query = useQuery({
    queryKey: [...ADMIN_LOGS_KEY, level, lines],
    queryFn: () => adminApi.logs({ lines, level: level || undefined }),
  });
  const rows = query.data?.lines ?? [];
  return (
    <AdminSection
      title="Server-Logs"
      testId="admin-logs"
      actions={
        <Button testId="admin-logs-refresh" disabled={query.isFetching} onClick={() => void query.refetch()}>
          {query.isFetching ? 'Lädt …' : 'Aktualisieren'}
        </Button>
      }
    >
      <div className="lfx-stack">
        <div className="lfx-admin-filters">
          <Field label="Level">
            {(id) => (
              <select id={id} className="lfx-select" value={level} data-testid="admin-logs-level" onChange={(e) => setLevel(e.target.value)}>
                {LEVELS.map((l) => (
                  <option key={l.value} value={l.value}>
                    {l.label}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field label="Zeilen">
            {(id) => (
              <select id={id} className="lfx-select" value={lines} data-testid="admin-logs-lines" onChange={(e) => setLines(Number(e.target.value))}>
                {LINE_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            )}
          </Field>
        </div>
        {query.isLoading && <Loading testId="admin-logs-loading" />}
        <AdminError error={query.error} testId="admin-logs-error" />
        {query.isSuccess && rows.length === 0 && <Empty testId="admin-logs-empty">Keine Log-Zeilen.</Empty>}
        {rows.length > 0 && (
          <ol className="lfx-admin-logs lfx-mono" data-testid="admin-logs-list" aria-label="Log-Zeilen">
            {rows.map((l, i) => (
              <li key={`${l.ts}-${i}`} className="lfx-admin-logline" data-testid="admin-logs-line">
                <span className="lfx-admin-logline__ts">{l.ts}</span>
                <Badge tone={levelTone(l.level)}>{l.level}</Badge>
                {l.context && <span className="lfx-admin-logline__ctx">[{l.context}]</span>}
                <span className="lfx-admin-logline__msg">{l.message}</span>
              </li>
            ))}
          </ol>
        )}
      </div>
    </AdminSection>
  );
}
